import {BASIC_COINS_INCOME} from "../../configs/income";
import {isEnvConfEnabled} from "../env/isEnvConfEnabled";
import {ENV_CONFIG_VARIABLES} from "../../constants/env";
import {getPlayerHexColor} from "../players/getPlayerHexColor";
import {STARTING_COINS, STARTING_COINS_DEV_MODE} from "../../configs/game";
import {getRandomElement} from "../other/getRandomElement";
import {GAME_TYPES} from "../../constants/game";
import {FACTIONS} from "../../constants/factions";
import {isPlayerFaction} from "../factions/isPlayerFaction";
import {ENLIGHTENED_STARTING_COINS_BONUS} from "../../configs/factions/enlightened";
import {FACTION_IDS_IMPLEMENTED} from "../../configs/factions";
import {getPlayerBonusValues} from "./getPlayerBonusValues";
import {checkIfPlayerBonusApplies} from "./checkIfPlayerBonusApplies";
import {getLegacyPlayerBonusConfig} from "../../configs/playerBonusLegacy";
import {PLAYER_BONUS_TYPES} from "../../configs/playerBonus";

/**
 * @param {Object[]} players - игроки из SET_PLAYERS / MISSION_SPAWN
 * @param {string} gameType - getRulesGameType(gameState)
 * @param {Object} [options]
 * @param {boolean} [options.isPlayerBonusPerCityRule] -
 *   checkIfPlayerBonusPerCityRule(gameState); false — запись, сделанная до
 *   бонуса за каждый город: игрок с бонусом без зафиксированных чисел получает
 *   прежние (LEGACY_PLAYER_BONUS_CONFIG)
 */
const createPlayersState = (players, gameType, {isPlayerBonusPerCityRule = true} = {}) => {
  return players.map(({
    id, name, color, factionId, selectedSkin, isBot, bonus, bonusConfig,
    hasUnitSkin, leagueId, isVisibleBot, isLocalBot, hasLobbyBonus,
    extraStartingCoins, isAi, hasLowRatingAdvantage, botSupplySizeBonus,
  }) => {
    const effectiveFactionId = factionId || getRandomElement(FACTION_IDS_IMPLEMENTED);
    const player = {
      id: id ? id : 'player_id_' + color,
      name,
      color,
      isBot: Boolean(isBot),
      factionId: effectiveFactionId,
      selectedSkin: selectedSkin || `${effectiveFactionId}_default`,
      hasUnitSkin: Boolean(hasUnitSkin),
      // Поля пола d6 (botD6MinRoll) больше нет: подкрутку костей боту дает его
      // уровень бонуса (см. getBotD6MinRoll). В старых записях поле еще
      // приходит — здесь оно отбрасывается
      // Прибавка к росту отрядов за снабжение у явного бота старшей лиги и у
      // бота локальной партии с таким же бонусом (см.
      // configs/botLeagueSupplyGrowth). Числа зафиксированы на старте и живут
      // в записи матча. Ставят ее только сервер явному боту и
      // prepareLocalMatchPlayers боту — поэтому без проверки isBot: тот
      // вырезается из публичных записей (см. hideBotMarksInRecordingActions),
      // и реплей у участника и в витрине меню растил бы отряды бота медленнее,
      // чем в живом матче
      ...(botSupplySizeBonus ? {botSupplySizeBonus} : {}),
      // Лига игрока на момент старта РЕЙТИНГОВОЙ партии: по ней боты решают,
      // давить ли этого игрока сообща (см. applyBotPressureLatches). В
      // остальных режимах поля нет — давление не включается
      ...(leagueId ? {leagueId} : {}),
      // «Явный» бот рейтингового матча: помечен ботом для всех игроков и играет
      // с экономическим бонусом (см. configs/visibleRankedBots). Поля нет у
      // обычных игроков, поэтому и в состоянии оно появляется только у таких
      ...(isVisibleBot ? {isVisibleBot: true} : {}),
      // Бот локальной партии: помечен значком с иконкой по выбранному для него
      // бонусу (см. checkIfBotMarkShown). Признак ставится только боту — у
      // человека с бонусом значка быть не должно
      ...(isLocalBot && isBot ? {isLocalBot: true} : {}),
      // LLM-агент промо-матча «Битва с AI» (User.isAi): по признаку везде
      // показывается метка ИИ. Это не бот — ботом его делает только маска
      // автопилота во view сервера. Поля нет у остальных игроков
      ...(isAi ? {isAi: true} : {}),
      // Фора новичкам: живой игрок с рейтингом ниже порога на старте партии
      // (см. configs/lowRatingAdvantage). Отряды ботов и нейтральные отряды
      // бросают против него кости урона со штрафом, а боты в партии с ним не
      // покупают «Экоанархизм». Признак — только живому: боту и ИИ-игроку
      // его не ставят, даже если прислали. Поля нет у остальных игроков
      ...(hasLowRatingAdvantage && !isBot && !isAi ? {hasLowRatingAdvantage: true} : {}),
      bonus: bonus || 'none',
      // Бонус сетевого лобби, выбранный создателем на всех игроков (см.
      // utils/lobby/lobbyBonus): включает бонус в сетевой игре и в реплее.
      // Поля нет у игроков без такого бонуса
      ...(hasLobbyBonus ? {hasLobbyBonus: true} : {}),
      // Зафиксированные числа бонуса (см. getPlayerBonusValues). Есть у «явных»
      // ботов, у игроков с бонусом лобби и с 29.10 у игроков локальной партии
      // (см. prepareLocalMatchPlayers) — у остальных бонус берется из текущего
      // конфига
      ...(bonusConfig ? {bonusConfig} : {}),
      hexColor: getPlayerHexColor(color),
      coins: gameType === GAME_TYPES.LOCAL && isEnvConfEnabled(ENV_CONFIG_VARIABLES.REACT_APP_ADDITIONAL_COINS) ? STARTING_COINS_DEV_MODE : STARTING_COINS, // Начальное количество монет
      coinsIncome: BASIC_COINS_INCOME, // Доход монет в конце раунда
      winPoints: 0,  // Начальное количество очков победы
      winPointsIncome: 0, // Доход очков в конце раунда
      constructionPhaseActions: 0,
      supplyPoints: 0 // Очки снабжения от ферм
    }

    if (isPlayerFaction(player, FACTIONS.ENLIGHTENED)) {
      player.coins += ENLIGHTENED_STARTING_COINS_BONUS;
    }

    if (isPlayerFaction(player, FACTIONS.GIANTS)) {
      // Тумблер авто-раздачи снабжения (см. GiantsAutoSupplyToggle): выключен -
      // фазу снабжения все фермы отдают только базовое снабжение поровну между
      // великанами; включен - алгоритм пытается копить доп. снабжение за 4 фермы
      // крупнейшему подходящему отряду. По умолчанию выключен (в т.ч. для ботов -
      // ИИ-логика для этой механики не реализована)
      player.isGiantsAutoAdditionalSupplyOn = false;
    }

    if (checkIfPlayerBonusApplies(player, gameType)) {
      // Запись, сделанная до бонуса за каждый город: числа, которых в игроке
      // нет, — прежние, как тогда (а не текущего конфига)
      const legacyBonusConfig = !isPlayerBonusPerCityRule && !player.bonusConfig
        && player.bonus !== PLAYER_BONUS_TYPES.NONE
        ? getLegacyPlayerBonusConfig(player.bonus)
        : null;
      if (legacyBonusConfig) {
        player.bonusConfig = {...legacyBonusConfig};
      }
      player.coins += getPlayerBonusValues(player).startingCoinsBonus;
    }

    // Надбавка к стартовым монетам, заданная сценарием (глава обучения
    // рассчитывает бюджет под свои шаги — см. playerExtraStartingCoins)
    if (extraStartingCoins > 0) {
      player.coins += extraStartingCoins;
    }

    return player;
  });
};
export {createPlayersState};